import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import TimelineSwitcher from '../Common/TimelineSwitcher';

const { 
  FiChevronDown, 
  FiChevronRight, 
  FiPackage, 
  FiDollarSign,
  FiCheckCircle,
  FiClock
} = FiIcons;

const CustomerProductTable = ({ customerId }) => {
  const [timeline, setTimeline] = useState('MAT');
  const [expandedGroups, setExpandedGroups] = useState(new Set(['abiraterone-acetate']));
  const navigate = useNavigate();

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleProductClick = (productName) => {
    const productId = productName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/product/${productId}`);
  };

  // Mock product data grouped by product family
  const productGroups = [
    {
      id: 'abiraterone-acetate',
      name: 'Abiraterone Acetate Tablets',
      totalSales: 8500000,
      products: [
        {
          strength: '250mg',
          packSize: '30 count',
          packCount: 30,
          ndc: '72578-001-30',
          awardStatus: 'Primary',
          contractPrice: 145.50,
          awardedQty: 125000,
          qtyActualPacks: 110000,
          sales12m: 5200000
        },
        {
          strength: '500mg',
          packSize: '60 count',
          packCount: 60,
          ndc: '72578-001-60',
          awardStatus: 'Secondary',
          contractPrice: 285.75,
          awardedQty: 85000,
          qtyActualPacks: 75000,
          sales12m: 3300000
        }
      ]
    },
    {
      id: 'atorvastatin',
      name: 'Atorvastatin Tablets',
      totalSales: 6200000,
      products: [
        {
          strength: '10mg',
          packSize: '90 count',
          packCount: 90,
          ndc: '72578-002-90',
          awardStatus: 'Primary',
          contractPrice: 28.90,
          awardedQty: 450000,
          qtyActualPacks: 400000,
          sales12m: 2100000
        },
        {
          strength: '20mg',
          packSize: '90 count',
          packCount: 90,
          ndc: '72578-002-91',
          awardStatus: 'Primary',
          contractPrice: 32.50,
          awardedQty: 380000,
          qtyActualPacks: 350000,
          sales12m: 2050000
        },
        {
          strength: '40mg',
          packSize: '90 count',
          packCount: 90,
          ndc: '72578-002-92',
          awardStatus: 'Primary',
          contractPrice: 38.75,
          awardedQty: 320000,
          qtyActualPacks: 300000,
          sales12m: 2050000
        }
      ]
    },
    {
      id: 'rosuvastatin',
      name: 'Rosuvastatin Tablets',
      totalSales: 4800000,
      products: [
        {
          strength: '5mg',
          packSize: '30 count',
          packCount: 30,
          ndc: '72578-003-30',
          awardStatus: 'Primary',
          contractPrice: 45.20,
          awardedQty: 280000,
          qtyActualPacks: 250000,
          sales12m: 1600000
        },
        {
          strength: '10mg',
          packSize: '30 count',
          packCount: 30,
          ndc: '72578-003-31',
          awardStatus: 'Secondary',
          contractPrice: 52.80,
          awardedQty: 220000,
          qtyActualPacks: 200000,
          sales12m: 1550000
        },
        {
          strength: '20mg',
          packSize: '30 count',
          packCount: 30,
          ndc: '72578-003-32',
          awardStatus: 'Primary',
          contractPrice: 58.90,
          awardedQty: 195000,
          qtyActualPacks: 180000,
          sales12m: 1650000
        }
      ]
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getAwardStatusColor = (status) => {
    const colors = {
      'Primary': 'bg-green-100 text-green-800',
      'Secondary': 'bg-yellow-100 text-yellow-800',
      'Tertiary': 'bg-orange-100 text-orange-800',
      'Not Awarded': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Current Awards Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                Current Award Status & Sales Performance
              </h3>
              <p className="text-gray-600 mt-1">Active contracts and sales data</p>
            </div>
            <TimelineSwitcher
              value={timeline}
              onChange={setTimeline}
              options={['MAT', 'YTD', 'YOY', 'Last Quarter', 'Current Month']}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Product Group</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Strength & Pack Size</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">NDC</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900">Award Status</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Contract Price</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Awarded Qty</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Qty Actual (Packs)</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Qty Actual (Eaches)</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Sales (12M)</th>
              </tr>
            </thead>
            <tbody>
              {productGroups.map((group) => (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <tr className="bg-blue-50 border-b border-gray-200 cursor-pointer hover:bg-blue-100 transition-colors">
                    <td 
                      colSpan="9" 
                      className="py-4 px-6"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <SafeIcon 
                            icon={expandedGroups.has(group.id) ? FiChevronDown : FiChevronRight}
                            className="text-blue-600"
                          />
                          <SafeIcon icon={FiPackage} className="text-blue-600" />
                          <span className="font-semibold text-blue-900">{group.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-blue-900">
                            Total Sales: {formatCurrency(group.totalSales)}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Product Detail Rows */}
                  {expandedGroups.has(group.id) && group.products.map((product, index) => (
                    <tr 
                      key={`${group.id}-${index}`}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleProductClick(group.name)}
                    >
                      <td className="py-4 px-6 pl-12">
                        <span className="text-sm text-gray-600">→ Click to view details</span>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-gray-900">{product.strength}</p>
                          <p className="text-sm text-gray-600">{product.packSize}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {product.ndc}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getAwardStatusColor(product.awardStatus)}`}>
                          {product.awardStatus}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">
                        {formatCurrency(product.contractPrice)}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700">
                        {formatNumber(product.awardedQty)}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700">
                        {formatNumber(product.qtyActualPacks)}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700">
                        {formatNumber(product.qtyActualPacks * product.packCount)}
                      </td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">
                        {formatCurrency(product.sales12m)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerProductTable;